import React, { useState, useEffect } from 'react';
import { Layout, Card, Row, Col, Button, Modal, Space, Typography, Dropdown, Menu } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MoreOutlined, FullscreenOutlined } from '@ant-design/icons';
import ChartBuilder from './ChartBuilder';
import ChartPreview from './ChartPreview';
import './Dashboard.css';

const { Header, Content } = Layout;
const { Title } = Typography;

const Dashboard = () => {
  const [charts, setCharts] = useState([]);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingChart, setEditingChart] = useState(null);
  const [dashboardTitle, setDashboardTitle] = useState('My Dashboard');

  // Sample charts for demonstration
  useEffect(() => {
    const sampleCharts = [
      {
        id: '1',
        title: 'Sales by Region',
        type: 'bar',
        data: [
          { region: 'North', sales: 12000, month: 'Jan' },
          { region: 'South', sales: 15000, month: 'Jan' },
          { region: 'East', sales: 18000, month: 'Jan' },
          { region: 'West', sales: 14000, month: 'Jan' }
        ],
        config: {
          xAxis: { name: 'region', type: 'string' },
          yAxis: { name: 'sales', type: 'number' }
        },
        size: { span: 12 }
      },
      {
        id: '2',
        title: 'Monthly Trends',
        type: 'line',
        data: [
          { month: 'Jan', revenue: 45000, expenses: 35000 },
          { month: 'Feb', revenue: 52000, expenses: 38000 },
          { month: 'Mar', revenue: 48000, expenses: 36000 },
          { month: 'Apr', revenue: 58000, expenses: 42000 },
          { month: 'May', revenue: 62000, expenses: 45000 }
        ],
        config: {
          xAxis: { name: 'month', type: 'string' },
          yAxis: { name: 'revenue', type: 'number' }
        },
        size: { span: 12 }
      },
      {
        id: '3',
        title: 'Product Categories',
        type: 'pie',
        data: [
          { category: 'Electronics', value: 35 },
          { category: 'Clothing', value: 28 },
          { category: 'Books', value: 20 },
          { category: 'Home', value: 17 }
        ],
        config: {
          xAxis: { name: 'category', type: 'string' },
          yAxis: { name: 'value', type: 'number' }
        },
        size: { span: 8 }
      },
      {
        id: '4',
        title: 'Performance Metrics',
        type: 'scatter',
        data: [
          { effort: 85, performance: 92, team: 'A' },
          { effort: 78, performance: 85, team: 'B' },
          { effort: 92, performance: 95, team: 'A' },
          { effort: 70, performance: 75, team: 'C' },
          { effort: 88, performance: 90, team: 'B' }
        ],
        config: {
          xAxis: { name: 'effort', type: 'number' },
          yAxis: { name: 'performance', type: 'number' },
          color: { name: 'team', type: 'string' }
        },
        size: { span: 16 }
      }
    ];
    setCharts(sampleCharts);
  }, []);

  const handleAddChart = () => {
    setEditingChart(null);
    setIsBuilderOpen(true);
  };

  const handleEditChart = (chart) => {
    setEditingChart(chart);
    setIsBuilderOpen(true);
  };

  const handleDeleteChart = (chartId) => {
    setCharts(prev => prev.filter(chart => chart.id !== chartId));
  };

  const handleSaveChart = (chartData) => {
    if (editingChart) {
      // Update existing chart
      setCharts(prev => prev.map(chart => 
        chart.id === editingChart.id ? { ...chart, ...chartData } : chart
      ));
    } else {
      // Add new chart
      const newChart = {
        id: Date.now().toString(),
        size: { span: 12 },
        ...chartData
      };
      setCharts(prev => [...prev, newChart]);
    }
    setIsBuilderOpen(false);
    setEditingChart(null);
  };

  const handleResizeChart = (chartId, newSize) => {
    setCharts(prev => prev.map(chart => 
      chart.id === chartId ? { ...chart, size: newSize } : chart
    ));
  };

  const getChartMenu = (chart) => (
    <Menu>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEditChart(chart)}>
        Edit Chart
      </Menu.Item>
      <Menu.Item key="fullscreen" icon={<FullscreenOutlined />}>
        Full Screen
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="small" onClick={() => handleResizeChart(chart.id, { span: 8 })}>
        Small Size
      </Menu.Item>
      <Menu.Item key="medium" onClick={() => handleResizeChart(chart.id, { span: 12 })}>
        Medium Size
      </Menu.Item>
      <Menu.Item key="large" onClick={() => handleResizeChart(chart.id, { span: 16 })}>
        Large Size
      </Menu.Item>
      <Menu.Item key="full" onClick={() => handleResizeChart(chart.id, { span: 24 })}>
        Full Width
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item 
        key="delete" 
        icon={<DeleteOutlined />} 
        danger
        onClick={() => handleDeleteChart(chart.id)}
      >
        Delete Chart
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
              {dashboardTitle}
            </Title>
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddChart}>
                Add Chart
              </Button>
              <Button>Save Dashboard</Button>
              <Button>Export</Button>
            </Space>
          </Col>
        </Row>
      </Header>

      <Content style={{ padding: '24px' }}>
        {charts.length === 0 ? (
          <div className="empty-dashboard">
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Title level={4} type="secondary">
                No charts yet
              </Title>
              <p style={{ color: '#666', marginBottom: '24px' }}>
                Start building your dashboard by adding your first chart
              </p>
              <Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleAddChart}>
                Create Your First Chart
              </Button>
            </div>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {charts.map(chart => (
              <Col key={chart.id} span={chart.size.span}>
                <Card
                  title={chart.title}
                  extra={
                    <Dropdown overlay={getChartMenu(chart)} trigger={['click']}>
                      <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                  }
                  style={{ height: '400px' }}
                  bodyStyle={{ height: '320px', padding: '16px' }}
                >
                  <ChartPreview
                    type={chart.type}
                    data={chart.data}
                    config={chart.config}
                  />
                </Card>
              </Col>
            ))}
            
            <Col span={8}>
              <Card
                className="add-chart-card"
                style={{ 
                  height: '400px', 
                  border: '2px dashed #d9d9d9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                onClick={handleAddChart}
                bodyStyle={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <PlusOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Title level={4} type="secondary">
                  Add New Chart
                </Title>
              </Card>
            </Col>
          </Row>
        )}
      </Content>

      <Modal
        title={editingChart ? 'Edit Chart' : 'Create New Chart'}
        open={isBuilderOpen}
        onCancel={() => setIsBuilderOpen(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ height: 'calc(100vh - 120px)', padding: 0 }}
      >
        <ChartBuilder
          initialChart={editingChart}
          onSave={handleSaveChart}
          onCancel={() => setIsBuilderOpen(false)}
        />
      </Modal>
    </Layout>
  );
};

export default Dashboard;